import { createElement } from 'react';

import {
  blockFormatExtension,
  boldExtension,
  createEditorSystem,
  floatingToolbarExtension,
  historyExtension,
  imageExtension,
  italicExtension,
  linkExtension,
  listExtension,
  underlineExtension,
} from '@lexkit/editor';

import { alignmentExtension } from './alignmentExtension';
import { FloatingToolbar } from './FloatingToolbar';

/**
 * Extension set for the notes rich text editor.
 * Keep the tuple `as const` so LexKit can infer the command/state types.
 */
export const richTextExtensions = [
  historyExtension,
  blockFormatExtension,
  boldExtension,
  italicExtension,
  underlineExtension,
  listExtension,
  linkExtension,
  alignmentExtension,
  imageExtension.configure({ resizable: false }),
  floatingToolbarExtension.configure({
    render: (props) => createElement(FloatingToolbar, props),
    positionStrategy: 'below',
  }),
] as const;

export type RichTextExtensions = typeof richTextExtensions;

/**
 * Class names applied to Lexical nodes by the editor theme.
 * These are targeted by `richTextContentSx` to match the app's design tokens.
 */
export const richTextEditorTheme = {
  paragraph: 'rt-paragraph',
  list: {
    ul: 'rt-list-ul',
    ol: 'rt-list-ol',
    listitem: 'rt-list-item',
    nested: {
      listitem: 'rt-list-item-nested',
    },
  },
  link: 'rt-link',
  text: {
    bold: 'rt-text-bold',
    italic: 'rt-text-italic',
    underline: 'rt-text-underline',
  },
};

/**
 * Typed Provider + hook pair bound to our extension set.
 * Import `useRichTextEditor` (re-exported from index) inside Provider children.
 */
export const { Provider: RichTextProvider, useEditor: useRichTextEditor } =
  createEditorSystem<RichTextExtensions>();
